import type { GridApiPremium } from '@mui/x-data-grid-premium';
import {
  GRID_ROOT_GROUP_ID,
  gridRowNodeSelector,
  gridRowSelectionIdsSelector,
} from '@mui/x-data-grid-premium';
import type { GroupHeaderRow } from 'src/components';
import { getIsGroupHeaderRow } from 'src/components/reports/types';
import type { Apollo } from 'src/gql/generated';

export interface SelectedGroup {
  docCount: number;
  groupRowId: string;
  groupSelection: Apollo.GroupSelectionInput;
}

/**
 * Per-group "select all": when exactly one group is the selection root (its
 * header checkbox fully checked, no sibling group independently selected),
 * derive the `groupSelection` so a bulk action applies to every document in the
 * group server-side — not just the loaded rows. Returns null when zero or
 * several groups are selected, so callers fall back to enumerated IDs or the
 * global select-all flag. `docCount` is the group's document total (for the
 * confirmation message).
 *
 * Reads the live selection imperatively; call it at action time, or in render
 * for a component that re-renders on selection change.
 */
export function getSelectedGroup(
  apiRef: React.RefObject<GridApiPremium | null>
): SelectedGroup | null {
  const api = apiRef.current;
  if (!api) return null;
  const selected = new Set([...gridRowSelectionIdsSelector(apiRef).keys()].map(String));
  if (selected.size === 0) return null;

  const rootGroupIds = [...selected].filter(id => {
    const node = gridRowNodeSelector(apiRef, id);
    if (node?.type !== 'group') return false;
    const parent = node.parent;
    return !(parent && parent !== GRID_ROOT_GROUP_ID && selected.has(String(parent)));
  });
  if (rootGroupIds.length !== 1) return null;

  return buildGroupSelection({
    getGroupHeaderRow: rowId => {
      const node = gridRowNodeSelector(apiRef, rowId);
      const row = api.getRow(rowId);
      if (node?.type !== 'group' || !row || !getIsGroupHeaderRow(row)) return null;
      return row;
    },
    getParentGroupRowId: rowId => {
      const parent = gridRowNodeSelector(apiRef, rowId)?.parent;
      return parent && parent !== GRID_ROOT_GROUP_ID ? String(parent) : null;
    },
    groupRowId: rootGroupIds[0],
  });
}

/**
 * Walks a group row up to the root, collecting the path the server re-resolves
 * membership from. Split out from the grid plumbing above so the contract
 * `getDocumentsToUpdate` depends on — the column ids and keys in outermost-first
 * order, and the zone the grid grouped in — is exercisable on its own.
 *
 * Returns null if any level of the path is not a group header row, rather than
 * a selection that names fewer levels than the group the user picked.
 */
export function buildGroupSelection({
  getGroupHeaderRow,
  getParentGroupRowId,
  groupRowId,
}: {
  getGroupHeaderRow: (rowId: string) => GroupHeaderRow | null;
  getParentGroupRowId: (rowId: string) => string | null;
  groupRowId: string;
}): SelectedGroup | null {
  const groupFields: string[] = [];
  const groupKeys: string[] = [];

  let rowId: string | null = groupRowId;
  while (rowId) {
    const row = getGroupHeaderRow(rowId);
    if (!row) return null;
    // Outermost level first, matching the order groupFields is grouped in.
    groupFields.unshift(row.groupFieldId);
    groupKeys.unshift(row.groupKey);
    rowId = getParentGroupRowId(rowId);
  }
  if (!groupFields.length) return null;

  return {
    docCount: getGroupHeaderRow(groupRowId)?.documentCount ?? 0,
    groupRowId,
    groupSelection: {
      groupFields,
      groupKeys,
      /**
       * The grid groups date columns by calendar day in the viewer's zone (the
       * same one `useReportDataSource` sends as `filterTimeZone`). The server
       * has to resolve membership in that zone or a group selected near
       * midnight would resolve to a different set of documents.
       */
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  };
}
